/**
 * @graphql agg
 */
import { useCallback } from 'react';
import { graphql } from 'react-relay';
import gql from 'graphql-tag';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';
import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';

import { useContentType } from '@confluence/page-context';
import type { PageStateType } from '@confluence/page-context';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { containsNetworkError } from '@confluence/network';
import { setContentErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

// Import types for the viewed comments query
import type {
	useViewedCommentsQuery,
	useViewedCommentsQueryVariables,
	ConfluenceAnalyticsCommentContentType,
} from './__types__/useViewedCommentsQuery.ts';

const ContentAnalyticsViewedComments = gql`
	query useViewedCommentsQuery(
		$contentId: ID!
		$contentType: ConfluenceAnalyticsCommentContentType!
	) {
		contentAnalyticsViewedComments(contentId: $contentId, contentType: $contentType) {
			commentIds
		}
	}
`;

export interface UseViewedCommentsResult {
	getViewedCommentsCacheOnly: () => {
		success: boolean;
		data?: Set<string>;
		error?: Error;
	};
	getViewedCommentsFresh: () => Promise<{ success: boolean; data?: Set<string>; error?: Error }>;
	updateViewedCommentsCache: (commentIds: string[]) => void;
}

// Type for the ViewedComments fragment data
interface ViewedCommentsFragmentData {
	commentIds: string[];
	__typename: 'ViewedComments';
}

// Helper function to safely convert content type for the viewed comments query
const getValidContentTypeForViewedComments = (
	contentType: PageStateType['contentType'],
): ConfluenceAnalyticsCommentContentType => {
	// Only page, database, and whiteboard support the viewed comments query
	if (contentType === 'page' || contentType === 'database' || contentType === 'whiteboard') {
		return contentType as ConfluenceAnalyticsCommentContentType;
	}
	// Fallback to page for other content types (blogpost, embed, etc.)
	return 'page' as ConfluenceAnalyticsCommentContentType;
};

// Helper to generate stable ROOT_QUERY cache key
// Ensures consistent key format regardless of object property order
const getRootQueryCacheId = (pageId: string, contentType: PageStateType['contentType']): string => {
	const validContentType = getValidContentTypeForViewedComments(contentType);
	// Sort keys to ensure stable JSON.stringify output
	const variables = {
		contentId: pageId,
		contentType: validContentType,
	};
	return `$ROOT_QUERY.contentAnalyticsViewedComments(${JSON.stringify(variables)})`;
};

export const useViewedComments = ({ pageId }: { pageId: string }): UseViewedCommentsResult => {
	const [contentType] = useContentType();
	const writeFragmentInterop = useWriteFragmentInterop<ViewedCommentsFragmentData>({
		fragmentSpec: graphql`
			fragment useViewedComments_write on ViewedComments {
				commentIds
			}
		`,
	});

	const readFragmentInterop = useReadFragmentInterop<ViewedCommentsFragmentData>({
		fragmentSpec: graphql`
			fragment useViewedComments_read on ViewedComments {
				commentIds
			}
		`,
	});

	// Network-only query for fresh data
	const { refetch } = useQueryInterop<useViewedCommentsQuery, useViewedCommentsQueryVariables>(
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		ContentAnalyticsViewedComments,
		{
			skip: true, // Never query on render
			fetchPolicy: 'network-only',
			variables: {
				contentId: pageId,
				contentType: getValidContentTypeForViewedComments(contentType),
			},
		},
	);

	// Helper function to process the query result
	const processQueryResult = useCallback((data: useViewedCommentsQuery) => {
		const commentIds = data?.contentAnalyticsViewedComments?.commentIds ?? [];
		const viewedCommentsSet = new Set<string>(
			commentIds.filter((id: string | null): id is string => id !== null),
		);

		return {
			success: true,
			data: viewedCommentsSet,
		};
	}, []);

	// Helper function for cache-only queries
	const executeCacheOnlyQuery = useCallback(() => {
		if (!pageId || !contentType) {
			throw new Error('Page ID and content type are required');
		}

		try {
			// Read from ROOT_QUERY (where Apollo stores query results)
			const rootQueryCacheId = getRootQueryCacheId(pageId, contentType);
			const rootQueryData = readFragmentInterop({ id: rootQueryCacheId });

			if (rootQueryData) {
				// Convert fragment data to query result format
				const queryResult: useViewedCommentsQuery = {
					contentAnalyticsViewedComments: {
						commentIds: rootQueryData.commentIds,
						__typename: 'ViewedComments' as const,
					},
				};
				return processQueryResult(queryResult);
			}

			return {
				success: false,
				error: new Error('No viewed comments data found in cache'),
			};
		} catch (error) {
			// Handle cache read errors - these are local operations so we just log unexpected errors
			setContentErrorAttributes(error, {
				objectId: pageId,
				objectType: 'page',
			});

			getMonitoringClient().submitError(error, {
				attribution: Attribution.COMMENTS,
			});

			markErrorAsHandled(error);
			return {
				success: false,
				error: error as Error,
			};
		}
	}, [pageId, contentType, processQueryResult, readFragmentInterop]);

	// Helper function for network-only queries
	const executeNetworkOnlyQuery = useCallback(async () => {
		if (!pageId) {
			throw new Error('Page ID and content type are required');
		}

		try {
			const result = await refetch({
				contentId: pageId,
				contentType: getValidContentTypeForViewedComments(contentType),
			});

			// Apollo automatically writes query results to ROOT_QUERY cache, so we don't need to explicitly write
			// The executeCacheOnlyQuery function will read from ROOT_QUERY directly
			return processQueryResult(result.data);
		} catch (error) {
			// Handle expected errors (403/unauthorized, network errors) - just mark as handled
			if (isUnauthorizedError(error) || containsNetworkError(error)) {
				markErrorAsHandled(error);
				return {
					success: false,
					error: error as Error,
				};
			}

			// Handle unexpected errors
			setContentErrorAttributes(error, {
				objectId: pageId,
				objectType: 'page',
			});

			getMonitoringClient().submitError(error, {
				attribution: Attribution.COMMENTS,
			});

			markErrorAsHandled(error);
			return {
				success: false,
				error: error as Error,
			};
		}
	}, [pageId, contentType, processQueryResult, refetch]);

	// Cached data query - uses cache-only for performance
	const getViewedCommentsCacheOnly = useCallback(() => {
		try {
			return executeCacheOnlyQuery();
		} catch (error) {
			return {
				success: false,
				error: error as Error,
			};
		}
	}, [executeCacheOnlyQuery]);

	// Fresh data query - always uses network-only for latest data
	const getViewedCommentsFresh = useCallback(
		() => executeNetworkOnlyQuery(),
		[executeNetworkOnlyQuery],
	);

	// Function to update the viewed comments cache
	// This is used for optimistic updates when comments are viewed locally
	const updateViewedCommentsCache = useCallback(
		(commentIds: string[]) => {
			if (!pageId || !contentType || commentIds.length === 0) {
				return;
			}

			try {
				// Read from ROOT_QUERY (where Apollo stores query results)
				const rootQueryCacheId = getRootQueryCacheId(pageId, contentType);
				const rootQueryData = readFragmentInterop({ id: rootQueryCacheId });
				const existingCommentIds = rootQueryData?.commentIds ?? [];

				// Merge new comment IDs with existing ones
				const existingSet = new Set(existingCommentIds.filter((id): id is string => id !== null));
				commentIds.forEach((id) => existingSet.add(id));
				const mergedCommentIds = Array.from(existingSet);

				// Update ROOT_QUERY cache
				writeFragmentInterop({
					fragmentId: rootQueryCacheId,
					data: {
						commentIds: mergedCommentIds,
						__typename: 'ViewedComments',
					},
				});
			} catch (error) {
				// Handle cache writing errors - these are local operations so we just log unexpected errors
				setContentErrorAttributes(error, {
					objectId: pageId,
					objectType: 'page',
				});

				getMonitoringClient().submitError(error, {
					attribution: Attribution.COMMENTS,
				});

				markErrorAsHandled(error);
			}
		},
		[pageId, contentType, readFragmentInterop, writeFragmentInterop],
	);

	return {
		getViewedCommentsCacheOnly,
		getViewedCommentsFresh,
		updateViewedCommentsCache,
	};
};
